// src/mobile_pages/contract/Sub.jsx

import { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { getSubContracts } from "../../api/contract/sub";
import { getSubStatusStatistics } from "../../api/contract/statistics";
import MobileBoardPagination from "../../components/common/board/MobileBoardPagination";
import Sub_view from "./Sub_view";

const STATUS_LABELS = {
  DRAFT: "임시",
  FOR_INFORMATION: "정보",
  FOR_REVIEW: "검토",
  FOR_APPROVAL: "승인",
  FINAL: "최종",
  AS_BUILT: "준공",
  NO_LONGER_IN_USE: "폐기",
};

const STATUS_COLORS = {
  DRAFT: "bg-gray-100 text-gray-600",
  FOR_INFORMATION: "bg-blue-100 text-blue-700",
  FOR_REVIEW: "bg-yellow-100 text-yellow-700",
  FOR_APPROVAL: "bg-purple-100 text-purple-700",
  FINAL: "bg-green-100 text-green-700",
  AS_BUILT: "bg-teal-100 text-teal-700",
  NO_LONGER_IN_USE: "bg-red-100 text-red-600",
};

export default function Sub() {
  const { id: projectId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const queryParams = new URLSearchParams(location.search);
  const constructionType = queryParams.get("construction_type") || "";

  const [statusStats, setStatusStats] = useState({});
  const [q, setQ] = useState("");
  const [searchField, setSearchField] = useState("title");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [searchTrigger, setSearchTrigger] = useState(0);

  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errMsg, setErrMsg] = useState("");
  const [total, setTotal] = useState(0);
  const [selectedId, setSelectedId] = useState(null);
  const [status, setStatus] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  // 날짜 필터 상태
  const [dateFilter, setDateFilter] = useState("updated_at");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const applySearch = () => {
    setPage(1);
    setSearchTrigger((prev) => prev + 1);
  };

  const handleReset = () => {
    setQ("");
    setSearchField("title");
    setStatus("");
    setDateFilter("updated_at");
    setStartDate(null);
    setEndDate(null);
    setPageSize(15);
    setPage(1);
    setSearchTrigger((prev) => prev + 1);
  };

  const fetchContracts = async () => {
    setLoading(true);
    try {
      const params = {
        project: projectId,
        construction_type: constructionType,
        [searchField]: q || undefined,
        status: status || undefined,
        date_filter: dateFilter || undefined,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        page,
        page_size: pageSize,
      };

      const res = await getSubContracts(projectId, params);
      setContracts(res.results ?? res);
      setTotal(res.count ?? res.total ?? res.length);
      setErrMsg("");
    } catch {
      setErrMsg("목록을 불러오지 못했습니다.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!projectId) return;
    async function fetchStatusStats() {
      try {
        const data = await getSubStatusStatistics(
          projectId,
          constructionType,
          "sub"
        );
        setStatusStats(data);
      } catch (e) {
        console.error("상태 통계 불러오기 실패:", e);
      }
    }
    fetchStatusStats();
  }, [projectId, constructionType]);

  useEffect(() => {
    fetchContracts();
  }, [
    projectId,
    constructionType,
    page,
    pageSize,
    searchTrigger,
    status,
    dateFilter,
    startDate,
    endDate,
  ]);

  const handleKeyPress = (e) => {
    if (e.key === "Enter") applySearch();
  };

  const handleRowClick = (contractId) => setSelectedId(contractId);

  const handleCreate = () => {
    navigate("new", { state: { construction_type: constructionType } });
  };

  const rows = useMemo(() => contracts ?? [], [contracts]);

  const statusOptions = [
    { key: "", label: "전체", count: statusStats?.전체 ?? 0 },
    { key: "DRAFT", label: "임시", count: statusStats?.status?.DRAFT ?? 0 },
    {
      key: "FOR_INFORMATION",
      label: "정보",
      count: statusStats?.status?.FOR_INFORMATION ?? 0,
    },
    {
      key: "FOR_REVIEW",
      label: "검토",
      count: statusStats?.status?.FOR_REVIEW ?? 0,
    },
    {
      key: "FOR_APPROVAL",
      label: "승인",
      count: statusStats?.status?.FOR_APPROVAL ?? 0,
    },
    { key: "FINAL", label: "최종", count: statusStats?.status?.FINAL ?? 0 },
    {
      key: "AS_BUILT",
      label: "준공",
      count: statusStats?.status?.AS_BUILT ?? 0,
    },
    {
      key: "NO_LONGER_IN_USE",
      label: "폐기",
      count: statusStats?.status?.NO_LONGER_IN_USE ?? 0,
    },
  ];

  const SkeletonCard = () => (
    <div className="bg-white rounded-lg p-4 animate-pulse border">
      <div className="flex justify-between items-start">
        <div className="flex-1 space-y-2">
          <div className="h-4 bg-gray-200 rounded w-3/4" />
          <div className="h-3 bg-gray-100 rounded w-1/2" />
        </div>
        <div className="h-6 w-12 bg-gray-100 rounded" />
      </div>
    </div>
  );

  return (
    <div className="mobile-page-root">
      <div className="mobile-page-root-inner space-y-3 mobile-bottom-spacing">
        {/* 헤더 */}
        <div className="bg-white rounded-lg p-4 border">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0 flex-1">
              <h2 className="text-lg font-bold text-gray-900 truncate">
                {constructionType || "전체"} 하도급 계약
              </h2>
              <p className="text-gray-500 text-xs mt-0.5">총 {total}건</p>
            </div>
            <button onClick={handleCreate} className="btn-create flex-shrink-0">
              등록
            </button>
          </div>
        </div>

        {errMsg && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-600">
            {errMsg}
          </div>
        )}

        {/* 상태 필터 - 2행 그리드 */}
        <div className="bg-white rounded-lg border p-2">
          <div className="grid grid-cols-4 gap-1.5">
            {statusOptions.map((opt) => (
              <button
                key={opt.key}
                onClick={() => {
                  setStatus(opt.key);
                  setPage(1);
                  setSearchTrigger((prev) => prev + 1);
                }}
                className={`px-2 py-1.5 rounded text-[10px] font-medium border transition-all text-center
                  ${status === opt.key
                    ? "bg-blue-600 text-white border-blue-600"
                    : "bg-white text-gray-600 border-gray-200 hover:border-blue-300"
                  }`}
              >
                <span className="block truncate">{opt.label}</span>
                <span className={`block ${status === opt.key ? "text-blue-200" : "text-gray-400"}`}>
                  ({opt.count})
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* 검색 영역 */}
        <div className="bg-white rounded-lg border overflow-hidden">
          <div className="p-3">
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={handleKeyPress}
                placeholder="계약명 검색..."
                className="flex-1 min-w-0 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <button onClick={applySearch} className="btn-search flex-shrink-0">
                검색
              </button>
            </div>
          </div>

          {/* 상세 필터 토글 */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="w-full px-4 py-2 bg-gray-50 border-t text-xs text-gray-600 text-center"
          >
            상세 필터 {showFilters ? "▲" : "▼"}
          </button>

          {showFilters && (
            <div className="px-3 pb-3 space-y-2 border-t bg-gray-50">
              <div className="pt-3 space-y-2">
                <div className="grid grid-cols-3 gap-2">
                  <select
                    value={searchField}
                    onChange={(e) => setSearchField(e.target.value)}
                    className="bg-white border border-gray-200 rounded-lg px-2 py-2 text-xs"
                  >
                    <option value="title">제목</option>
                    <option value="contract_code">계약코드</option>
                  </select>
                  <select
                    value={pageSize}
                    onChange={(e) => setPageSize(Number(e.target.value))}
                    className="bg-white border border-gray-200 rounded-lg px-2 py-2 text-xs"
                  >
                    <option value={15}>15건</option>
                    <option value={30}>30건</option>
                    <option value={50}>50건</option>
                  </select>
                  <button
                    onClick={handleReset}
                    className="bg-white border border-gray-200 rounded-lg px-2 py-2 text-xs text-gray-600"
                  >
                    초기화
                  </button>
                </div>

                {/* 날짜 필터 */}
                <div className="space-y-2">
                  <div className="text-[10px] font-medium text-gray-500">
                    날짜 필터
                  </div>
                  <select
                    value={dateFilter}
                    onChange={(e) => setDateFilter(e.target.value)}
                    className="w-full bg-white border border-gray-200 rounded-lg px-2 py-2 text-xs"
                  >
                    <option value="updated_at">수정일</option>
                    <option value="created_at">등록일</option>
                  </select>
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="date"
                      value={startDate || ""}
                      onChange={(e) => setStartDate(e.target.value || null)}
                      className="w-full bg-white border border-gray-200 rounded-lg px-2 py-2 text-xs"
                    />
                    <input
                      type="date"
                      value={endDate || ""}
                      onChange={(e) => setEndDate(e.target.value || null)}
                      className="w-full bg-white border border-gray-200 rounded-lg px-2 py-2 text-xs"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* 계약 리스트 */}
        <div className="space-y-2">
          {loading ? (
            <>
              <SkeletonCard />
              <SkeletonCard />
            </>
          ) : rows.length === 0 ? (
            <div className="bg-white rounded-lg p-8 text-center border">
              <p className="text-sm text-gray-500">등록된 계약이 없습니다.</p>
              <button onClick={handleCreate} className="btn-create mt-4">
                새 계약 등록
              </button>
            </div>
          ) : (
            rows.map((row) => (
              <button
                key={row.dns}
                onClick={() => handleRowClick(row.dns)}
                className="w-full text-left bg-white rounded-lg p-2 border hover:border-blue-300 active:bg-gray-50 transition-all"
              >
                {/* 상단: 상태 배지 + 제목 */}
                <div className="flex items-center gap-1 mb-1">
                  {row.meta?.status && (
                    <span
                      className={`px-1 py-px rounded text-[9px] font-medium flex-shrink-0 ${STATUS_COLORS[row.meta.status] || "bg-gray-100 text-gray-600"}`}
                    >
                      {STATUS_LABELS[row.meta.status]}
                    </span>
                  )}
                  {row.meta?.contract_code && (
                    <span className="text-[9px] text-gray-400 truncate">
                      {row.meta.contract_code}
                    </span>
                  )}
                </div>

                {/* 제목 */}
                <h3 className="text-[13px] font-medium text-gray-900 truncate mb-1">
                  {row.title || "제목 없음"}
                </h3>

                {/* 하단: 공사유형 + 버전 */}
                <div className="flex items-center gap-1 text-[11px] text-gray-400">
                  <span className="truncate">{row.meta?.construction_type || "-"}</span>
                  <span>·</span>
                  <span className="flex-shrink-0">Rev.{row.meta?.revision} v{row.meta?.version}</span>
                </div>
              </button>
            ))
          )}
        </div>

        {/* 페이지네이션 */}
        {!loading && rows.length > 0 && (
          <div className="bg-white rounded-lg p-3 border">
            <MobileBoardPagination
              page={page}
              pageSize={pageSize}
              total={total}
              onPageChange={setPage}
            />
          </div>
        )}

        {/* 상세 바텀시트 */}
        {selectedId && (
          <div
            className="fixed inset-0 z-50 bg-black/40"
            onClick={() => setSelectedId(null)}
          >
            <div
              className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl max-h-[85vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sticky top-0 bg-white pt-3 pb-2 px-4 border-b z-10">
                <div className="w-10 h-1 bg-gray-300 rounded-full mx-auto mb-3" />
                <div className="flex items-center justify-between gap-2">
                  <h3 className="text-base font-semibold truncate flex-1">
                    하도급 계약 상세
                  </h3>
                  <button
                    onClick={() => setSelectedId(null)}
                    className="px-3 py-1.5 text-sm text-gray-600 bg-gray-100 rounded-lg"
                  >
                    닫기
                  </button>
                </div>
              </div>
              <div className="p-4">
                <Sub_view
                  contractId={selectedId}
                  onClose={() => setSelectedId(null)}
                  onUpdated={(newId) => {
                    setSelectedId(newId);
                    fetchContracts();
                  }}
                  inBottomSheet={true}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
